import { z } from '@hono/zod-openapi'
import { Uuid } from './primitives'

/**
 * Where in a workflow a file was captured (UI plan Phase 2).
 *
 * This is a COLUMN on `attachments`, not a side table, and that is a
 * deliberate deviation from the plan's "add platform tables for attachment
 * contexts and annotation derivatives". A file has exactly one context — a
 * side table would permit the same photo to be simultaneously the BEFORE and
 * the AFTER of the same repair, which is not a state anyone should be able to
 * record — and it would need its own RLS policy, purge entry and governance
 * class to express a single enum. Same reasoning for derivatives: an annotated
 * photo IS an attachment, so it is a self-reference, not a join table.
 *
 * GENERAL is the default and the value every pre-existing row carries. It is
 * NOT a synonym for "unknown": the contexts below are claims about evidence,
 * and back-filling a claim onto historic files would be fabricating one.
 */
export const AttachmentContext = z
  .enum(['GENERAL', 'BEFORE', 'AFTER', 'FAILURE_EVIDENCE', 'REFERENCE'])
  .openapi('AttachmentContext')
export type AttachmentContext = z.infer<typeof AttachmentContext>

/**
 * There is deliberately **no `ATTACHMENT_CONTEXT_LABEL` here.**
 *
 * One existed and was deleted in localization Phase 5a (2026-08-02). It was a
 * `Record<AttachmentContext, string>` of English display text — `Failure
 * evidence` and four others — living in the package that defines the wire
 * contract, with exactly one consumer: the web gallery's thumbnail badge.
 *
 * Display strings cannot live here, because nothing in this package can reach a
 * locale: `@floorfile/contracts` is imported by the Worker, the SDK and the
 * OpenAPI generator, none of which have a requesting user's language. A label
 * map here is therefore permanently English, and its existence guarantees a
 * second English copy in whichever catalogue eventually translates the same
 * enum — two sources of truth for five strings, where the untranslatable one
 * wins by being the one already imported.
 *
 * The labels now live in `features/attachments/copy.ts`, keyed by the enum
 * value. Server-side text in Phase 8 gets them from the pure half of
 * `@floorfile/i18n` with the company's locale, not from a constant here.
 */

/**
 * The attachment summary carried on records that embed files (chat messages,
 * comments — SYSTEM_ARCHITECTURE §7). The full attachment DTO lives on the
 * platform routes; consumers embed just enough to render a chip and build
 * the download link (`/api/v1/attachments/{id}/content`).
 */
export const AttachmentSummary = z
  .object({
    id: Uuid,
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    /** Omitted when GENERAL — the overwhelming majority of rows. */
    context: AttachmentContext.optional(),
    /** Set on an annotation derivative; points at the untouched original. */
    derivedFromAttachmentId: Uuid.optional(),
  })
  .openapi('AttachmentSummary')
export type AttachmentSummary = z.infer<typeof AttachmentSummary>

/** `{ attachmentId, context }` — how a caller labels files it is claiming. */
export const AttachmentEvidenceRef = z
  .object({ attachmentId: Uuid, context: AttachmentContext })
  .strict()
  .openapi('AttachmentEvidenceRef')
export type AttachmentEvidenceRef = z.infer<typeof AttachmentEvidenceRef>

/**
 * The upload rules the server enforces on `POST /attachments`, shared so the
 * client can check a file BEFORE uploading it and name the real reason (F32:
 * a 0-byte file came back as "One or more fields are invalid." because the
 * field details never reached the screen).
 */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024
export const ATTACHMENT_MAX_FILE_NAME_LENGTH = 255
export const ATTACHMENT_MIME_ALLOWLIST = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  // Voice memos (UI plan Phase 2). MediaRecorder emits whichever of these the
  // platform supports and there is no negotiating it: Chrome/Firefox produce
  // `audio/webm`, Safari and iOS produce `audio/mp4`. The recorder also appends
  // a codecs parameter (`audio/webm;codecs=opus`), which the client strips
  // before declaring — this list is compared against the bare type.
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
] as const

export const CreateAttachmentInput = z
  .object({
    /** Offline clients may mint the id (D24) — queued parent ops reference
     * captured photos/signatures by this id before the upload ever syncs. */
    id: z.string().uuid().optional(),
    entityType: z.string().min(1).max(50),
    entityId: z.string().uuid(),
    module: z.string().min(1).max(50),
    fileName: z.string().min(1).max(ATTACHMENT_MAX_FILE_NAME_LENGTH),
    mimeType: z.string().min(1).max(120),
    sizeBytes: z.number().int().min(1),
    /**
     * Base64 SHA-256 of the bytes about to be uploaded (audit FF-08).
     *
     * OPTIONAL, and additive-only by design (v1 changes must be — API_SPEC
     * §versioning): a client that omits it gets exactly the previous
     * behaviour. When supplied, the server signs it into the presigned PUT so
     * R2 verifies the body against it and reports the digest back, which is
     * what lets `confirm` populate `attachments.checksum_sha256` on the
     * direct-to-R2 path. Without it that column is NULL for every file
     * uploaded in staging and production.
     *
     * 44 chars: base64 of a 32-byte digest, always `=`-padded.
     */
    checksumSha256: z
      .string()
      .regex(/^[A-Za-z0-9+/]{43}=$/, 'Expected base64-encoded SHA-256 (44 characters).')
      .optional(),
    /** Where in the workflow this was captured. Defaults to GENERAL. */
    context: AttachmentContext.optional(),
    /**
     * The attachment this one was derived from — set by the annotation editor,
     * which uploads a NEW flattened image and never touches the original.
     *
     * The server verifies the source is a stored, readable image in the same
     * company before accepting it, and re-derives nothing from it: a derivative
     * is an ordinary attachment that happens to name its parent. Chains are
     * refused (a derivative cannot itself be a source) so "the original" is
     * always exactly one hop away and evidence review never has to walk a tree.
     */
    derivedFromAttachmentId: Uuid.optional(),
  })
  .openapi('CreateAttachmentInput')
export type CreateAttachmentInput = z.infer<typeof CreateAttachmentInput>

export const Attachment = z
  .object({
    id: z.string().uuid(),
    entityType: z.string(),
    entityId: z.string().uuid(),
    module: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    status: z.string(),
    scanStatus: z.string(),
    checksumSha256: z.string().optional(),
    context: AttachmentContext,
    derivedFromAttachmentId: Uuid.optional(),
    uploadedBy: z.string().uuid().optional(),
    /** Resolved display name of the uploader, when the caller may see it. */
    uploadedByName: z.string().optional(),
    createdAt: z.string(),
  })
  .openapi('Attachment')
export type Attachment = z.infer<typeof Attachment>

export const CreateAttachmentResult = z
  .object({
    attachmentId: z.string().uuid(),
    uploadUrl: z.string(),
    maxBytes: z.number().int(),
  })
  .openapi('CreateAttachmentResult')
export type CreateAttachmentResult = z.infer<typeof CreateAttachmentResult>
